import {useState, type ReactNode} from 'react';
import {graphql, useFragment} from 'react-relay';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, NestedCard, Pill, PillVariant, Text} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {AcceptSuggestionPanel} from './AcceptSuggestionPanel';
import {relativeTime} from '../utils/relativeTime';
import {confidenceLabel} from '../utils/confidenceLabel';
import {formatDateShort} from '../utils/formatDate';
import {personDisplayName} from '../../../graphql/fragments/ticketingPerson';
import {useAiSummaryCollapse} from '../hooks/useAiSummaryCollapse';
import {useRegenerateAiSummary} from '../hooks/useRegenerateAiSummary';
import {useSubmitAiSummaryFeedback} from '../hooks/useSubmitAiSummaryFeedback';
import {useDismissSuggestedAction} from '../hooks/useDismissSuggestedAction';
import type {AiSummaryBlock_ticket$key} from './__generated__/AiSummaryBlock_ticket.graphql';

const AiSummaryFragment = graphql`
	fragment AiSummaryBlock_ticket on Ticket {
		id
		status
		aiSummaryState
		aiSummary {
			summary
			statusSignal
			sentiment
			commentCount
			emailCount
			generatedAt
			modelVersion
			feedbackScore
			feedbackText
			isFinal
			staleness {
				isStale
				newComments
				newEmails
				newStatusEvents
				total
			}
		}
		aiSuggestedActions {
			id
			title
			suggestedDueDate
			rationale
			confidence
			status
			assignee {
				id
				fullName
				initials
				profilePictureId
				active
				...ticketingPerson_person
			}
		}
	}
`;

const HeaderControls = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const Body = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[300]};
	padding: ${p => p.theme.sys.spacing[100]} ${p => p.theme.sys.spacing[200]} ${p => p.theme.sys.spacing[200]};
`;
// The suggested actions read as a row of quiet offers, not a stack of cards: each is
// one press, and the row wraps rather than scrolling.
const ActionRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const ExpandedPanel = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const ActionMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;
const NoteRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => p.theme.sys.spacing[100]};
`;
const NoteInput = styled.textarea`
	${p => p.theme.sys.font?.detail?.['1']}
	color: ${p => p.theme.sys.color.text.content.main};
	border: 1px solid ${p => p.theme.sys.color.border.decorative};
	border-radius: ${p => p.theme.sys.size.borderradius['2']};
	padding: ${p => p.theme.sys.spacing[100]} ${p => p.theme.sys.spacing[200]};
	resize: vertical;
	min-height: 48px;
`;

function pluralise(count: number, noun: string): string {
	return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

interface Props {
	ticketRef: AiSummaryBlock_ticket$key;
	/** True while a generation job is in flight and no summary exists yet. */
	generating?: boolean;
}

/**
 * The AI block: one card above the tabs, so what the model concluded and what it
 * proposes read as a single voice rather than two stacked panels.
 *
 * At rest it is the summary plus a row of suggested actions, each a single press.
 * Pressing one opens the accept panel beneath the row with the model's reasoning
 * (rationale, confidence, suggested assignee and date) and both ways out — add it
 * to the ticket, or dismiss the suggestion.
 */
export function AiSummaryBlock({ticketRef, generating = false}: Props) {
	const ticket = useFragment<AiSummaryBlock_ticket$key>(AiSummaryFragment, ticketRef);
	const {collapsed, toggle} = useAiSummaryCollapse(ticket.id);
	const {regenerate, inFlight, error} = useRegenerateAiSummary();
	const {submit: submitFeedback, inFlight: feedbackInFlight} = useSubmitAiSummaryFeedback();
	const {dismiss, inFlight: dismissing} = useDismissSuggestedAction();
	const summary = ticket.aiSummary;
	const state = ticket.aiSummaryState;
	const isFinal = Boolean(summary?.isFinal);
	const busy = generating || inFlight;

	// The current per-summary vote. Re-pressing the selected thumb clears it; the
	// "what was wrong?" note appears only while a downvote is active.
	const score = summary?.feedbackScore ?? null;
	const [showNote, setShowNote] = useState(false);
	const [note, setNote] = useState(summary?.feedbackText ?? '');
	const [expandedId, setExpandedId] = useState<string | null>(null);

	const vote = (next: 1 | -1) => {
		if (score === next) {
			setShowNote(false);
			submitFeedback({ticketId: ticket.id, score: null, feedbackText: null});
			return;
		}
		if (next === -1) {
			setShowNote(true);
			submitFeedback({ticketId: ticket.id, score: -1, feedbackText: summary?.feedbackText ?? null});
		} else {
			setShowNote(false);
			submitFeedback({ticketId: ticket.id, score: 1, feedbackText: null});
		}
	};

	// A resolved ticket keeps its summary as the record of what happened, but stops
	// proposing work.
	const isTerminal = ticket.status === 'resolved' || ticket.status === 'closed';
	const actions = isTerminal ? [] : ticket.aiSuggestedActions ?? [];
	const expandedAction = actions.find(a => a.id === expandedId) ?? null;

	const staleness = summary?.staleness;
	const stalenessTitle = staleness
		? `${pluralise(staleness.newComments, 'comment')}, ${pluralise(staleness.newEmails, 'email')}, ${pluralise(
				staleness.newStatusEvents,
				'status change'
		  )}`
		: undefined;

	const headerControls = summary ? (
		<>
			{!isFinal && staleness?.isStale && staleness.total > 0 && (
				<span title={stalenessTitle}>
					<Pill variant={PillVariant.INFORMATION}>{`${pluralise(staleness.total, 'new item')} since summary`}</Pill>
				</span>
			)}
			{isFinal && <Pill variant={PillVariant.NEUTRAL}>Final summary</Pill>}
			<Detail color="medium">{relativeTime(summary.generatedAt)}</Detail>
			{/* The design system has a `like` glyph but no `dislike`, so the vote reads in
			    words rather than as one glyph and one improvised opposite. */}
			<Button emphasis={EMPHASIS.LOW} aria-pressed={score === 1} disabled={feedbackInFlight} onPress={() => vote(1)}>
				Helpful
			</Button>
			<Button emphasis={EMPHASIS.LOW} aria-pressed={score === -1} disabled={feedbackInFlight} onPress={() => vote(-1)}>
				Not helpful
			</Button>
			{!isFinal && (
				<Button emphasis={EMPHASIS.LOW} disabled={busy} onPress={() => regenerate(ticket.id)}>
					{inFlight ? 'Regenerating…' : 'Regenerate'}
				</Button>
			)}
			<LabelledButton
				emphasis={EMPHASIS.LOW}
				icon={collapsed ? 'chevronDown' : 'chevronUp'}
				label={collapsed ? 'Expand summary' : 'Collapse summary'}
				aria-expanded={!collapsed}
				onPress={toggle}
			/>
		</>
	) : null;

	const shell = (children: ReactNode) => (
		<NestedCard colorTheme="light" dataCy="ai-summary">
			<NestedCard.Header mainIcon="sparkle" sectionName="Forecast AI" name="Summary">
				<HeaderControls>{headerControls}</HeaderControls>
			</NestedCard.Header>
			<NestedCard.UncollapsibleContentWrapper>
				<NestedCard.Content>
					<Body>{children}</Body>
				</NestedCard.Content>
			</NestedCard.UncollapsibleContentWrapper>
		</NestedCard>
	);

	// No thread yet — say so, never fabricate a summary.
	if (!summary && state === 'NO_ACTIVITY') {
		return shell(<Detail color="medium">AI summary will appear once the ticket has activity.</Detail>);
	}

	const retryRow = (
		<>
			<ActionRow>
				<Button emphasis={EMPHASIS.MONO} disabled={busy} onPress={() => regenerate(ticket.id)}>
					{inFlight ? 'Trying…' : 'Try again'}
				</Button>
			</ActionRow>
			{error && <Detail color="error">{error.message}</Detail>}
		</>
	);

	// The latest generation failed — a quiet error and a way to retry.
	if (!summary && state === 'FAILED') {
		return shell(
			<>
				<Detail color="medium">The AI summary couldn’t be generated. You can try again.</Detail>
				{retryRow}
			</>
		);
	}

	// First generation, or a regenerate of an empty block. Offers retry in case
	// generation is stuck (no failure event was recorded).
	if (!summary && (busy || state === 'PENDING')) {
		return shell(
			<>
				<Detail color="medium">{inFlight ? 'Regenerating…' : 'Generating summary…'}</Detail>
				{retryRow}
			</>
		);
	}

	// Ready but no row (defensive) — render nothing.
	if (!summary) return null;

	const provenance = `${pluralise(summary.commentCount, 'comment')} · ${pluralise(summary.emailCount, 'email')}`;

	return shell(
		<>
			{!collapsed && (
				<>
					<Text>{summary.summary}</Text>
					{actions.length > 0 && (
						<ActionRow data-testid="ai-suggested-actions">
							{actions.map(action => (
								<Button
									key={action.id}
									emphasis={EMPHASIS.MONO}
									onPress={() => setExpandedId(expandedId === action.id ? null : action.id)}
								>
									{action.title}
								</Button>
							))}
						</ActionRow>
					)}
					{expandedAction && (
						<ExpandedPanel data-testid="ai-action-detail">
							<ActionMeta>
								{expandedAction.assignee && (
									<Detail color="medium">{`Suggested owner: ${personDisplayName(
										expandedAction.assignee
									)}`}</Detail>
								)}
								{expandedAction.suggestedDueDate && (
									<Detail color="medium">{`Due ${formatDateShort(expandedAction.suggestedDueDate)}`}</Detail>
								)}
								{confidenceLabel(expandedAction.confidence) && (
									<Detail color="medium">{confidenceLabel(expandedAction.confidence)}</Detail>
								)}
							</ActionMeta>
							{expandedAction.rationale && <Detail color="medium">{expandedAction.rationale}</Detail>}
							<AcceptSuggestionPanel
								ticketId={ticket.id}
								action={{
									id: expandedAction.id,
									title: expandedAction.title,
									rationale: expandedAction.rationale ?? null,
									suggestedDueDate: expandedAction.suggestedDueDate ?? null,
									assignee: expandedAction.assignee
										? {
												id: expandedAction.assignee.id,
												fullName: expandedAction.assignee.fullName ?? null,
												initials: expandedAction.assignee.initials ?? null,
										  }
										: null,
								}}
								onCancel={() => setExpandedId(null)}
								onCompleted={() => setExpandedId(null)}
							/>
							<ActionRow>
								<Button
									emphasis={EMPHASIS.LOW}
									disabled={dismissing}
									onPress={() => {
										dismiss(ticket.id, expandedAction.id);
										setExpandedId(null);
									}}
								>
									Dismiss suggestion
								</Button>
							</ActionRow>
						</ExpandedPanel>
					)}
					<Detail color="medium">{provenance}</Detail>
				</>
			)}
			{showNote && !collapsed && (
				<NoteRow>
					<NoteInput
						value={note}
						placeholder="What was wrong? (optional)"
						onChange={e => setNote(e.target.value)}
						aria-label="What was wrong?"
					/>
					<ActionRow>
						<Button
							emphasis={EMPHASIS.MONO}
							disabled={feedbackInFlight}
							onPress={() => {
								submitFeedback({ticketId: ticket.id, score: -1, feedbackText: note.trim() || null});
								setShowNote(false);
							}}
						>
							Save note
						</Button>
					</ActionRow>
				</NoteRow>
			)}
			{error && <Detail color="error">{error.message}</Detail>}
		</>
	);
}
